import { FaPencil } from "react-icons/fa6";
import { GenericInfoField } from "../../../../common/helpers/generic-info-field";
import {
  IPurchaseOrder,
  TYPE_PAYMENT,
} from "../../../../types/purchaseOrder.types";
import { useEffect, useState } from "react";
import ModalEditTable from "../modals/ModalEditTable";
import { newMaskCurrency } from "../../../../common/helpers/mask.helpers";
import { getCompanyId } from "../../../../common/services/company.api";
import ContextMenu from "../../../../common/components/ui/context-menu/ContextMenu";
import Permission from "../../../../common/components/auth/Permission";
import { ActionItem } from "../../../../common/components/ui/context-menu/PopoverList";
import { HiOutlinePaperClip } from "react-icons/hi2";
import { HiOutlineDownload } from "react-icons/hi";
import {
  approvePurchaseOrder,
  rejectPurchaseOrder,
  cancelPurchaseOrder,
} from "../../../../common/services/purchaseOrder.api";
import { useToastContext } from "../../../../common/components/ui/toast/toast.context";
import { useUser } from "../../../../common/services/users.api";
import ModalConfirmStatus from "../modals/ModalConfirmStatus";
import ModalEditSubTable from "../modals/ModalEditSubTable";
import ModalEditBilletFiles from "../modals/ModalEditBilletFiles";
import { FaRegTrashAlt } from "react-icons/fa";
import ModalDeleteFile from "../modals/ModalDeleteFile";

type Props = {
  purchaseOrder: IPurchaseOrder;
};

const InfoTablePurchaseOrder = ({ purchaseOrder }: Props) => {
  const EMPTY_ACTION = { eventName: "", data: undefined };
  const [data, setData] = useState<IPurchaseOrder>(purchaseOrder);
  const [action, setAction] = useState<{
    eventName: string;
    data?: IPurchaseOrder;
  }>(EMPTY_ACTION);
  const [loading, setLoading] = useState(true);
  const [showDeleteFile, setShowDeleteFile] = useState("");

  useEffect(() => {
    if (action?.data) {
      setData(action.data);
    } else if (purchaseOrder) {
      setData(purchaseOrder);
    }
  }, [action?.data, purchaseOrder]);

  useEffect(() => {
    setLoading(!data);
  }, [data]);

  const onClose = (data: IPurchaseOrder) => {
    setAction({ eventName: "updated", data });
  };

  const isAction = (eventName: string) => action.eventName === eventName;
  const user = useUser();
  const { notifySuccess, notifyError } = useToastContext();

  const [modal, setModal] = useState<null | "approve" | "reject" | "remove">(
    null
  );
  const [selectedAction, setSelectedAction] = useState<
    null | "approve" | "reject" | "remove"
  >(null);

  const handleApprove = () => {
    setSelectedAction("approve");
    setModal("approve");
  };

  const handleReject = () => {
    setSelectedAction("reject");
    setModal("reject");
  };

  const handleRemove = () => {
    setSelectedAction("remove");
    setModal("remove");
  };

  const confirmApprove = async () => {
    setLoading(true);
    const result = await approvePurchaseOrder({
      _id: data._id ?? "",
      requestorEmail: user.email ?? "",
      requestorName: user.name ?? "",
    });
    setLoading(false);
    setModal(null);
    if (!result.ok) {
      notifyError(result.message ?? "Erro desconhecido");
      return;
    }
    notifySuccess(
      result.data.status === "approved"
        ? "Aprovado com sucesso"
        : "Pré-Aprovado com sucesso"
    );
    setAction({ eventName: "updated", data: result.data });
  };

  const confirmReject = async () => {
    setLoading(true);
    const result = await rejectPurchaseOrder(data._id ?? "");
    setLoading(false);
    setModal(null);
    if (!result.ok) {
      notifyError(result.message ?? "Erro desconhecido");
      return;
    }
    notifySuccess("Reprovado com sucesso");
    setAction({ eventName: "updated", data: result.data });
  };

  const confirmRemove = async () => {
    setLoading(true);
    const result = await cancelPurchaseOrder(data._id ?? "");
    setLoading(false);
    setModal(null);
    if (!result.ok) {
      notifyError(result.message ?? "Erro desconhecido");
      return;
    }
    notifySuccess("Removido com sucesso");
    setAction({ eventName: "updated", data: result.data });
  };

  const [showAllInstallments, setShowAllInstallments] = useState(false);
  const getStatusColor = (status: string) => {
    switch (status) {
      case "approved":
        return "bg-greenDark";
      case "pending":
        return "bg-bluePrimary";
      case "rejected":
        return "bg-redPrimary";
      case "pre-approved":
        return "bg-yellowPrimary";
      case "canceled":
        return "bg-gray";
      default:
        return "bg-gray-300";
    }
  };

  const STATUS_LABELS = {
    approved: "Aprovada",
    "pre-approved": "Pré-Aprovada",
    pending: "Pendente",
    rejected: "Reprovada",
    canceled: "Cancelada",
  };

  if (loading) {
    return (
      <section className="flex items-center justify-center min-h-[300px]">
        <div className="flex flex-col items-center gap-2">
          <div className="w-10 h-10 border-4 border-[#e44131] border-t-transparent rounded-full animate-spin"></div>
          <span className="text-[#e44131] font-semibold">
            Carregando informações...
          </span>
        </div>
      </section>
    );
  }

  const FileLink = ({ fileName }: { fileName: string }) => (
    <a
      href={`https://assets.locavibe.com.br/documents/nibo/${fileName}`}
      target="_blank"
      rel="noopener noreferrer"
      className="col-span-1 flex items-center gap-3 bg-white border border-gray-200 p-3 rounded shadow-sm hover:bg-gray-50 transition"
    >
      <span className="p-2 rounded-full flex items-center justify-center">
        <HiOutlinePaperClip className="h-5 w-5 text-black" />
      </span>
      <span className="flex-1 truncate text-blue-700 underline text-sm">
        {fileName}
      </span>
      <HiOutlineDownload className="h-4 w-4 text-gray-600" />
    </a>
  );

  return (
    <section className="border border-[#e44131] rounded-lg w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex justify-between items-center bg-[#e44131] p-6">
        <h2 className="font-semibold text-2xl text-white">
          Ordem de Compra: #{purchaseOrder.code}
        </h2>

        <div className="relative group">
          <button
            onClick={() => {
              if (getCompanyId() == "*") return;
              setAction({ eventName: "edit", data: data });
            }}
            aria-label="Editar"
            className="bg-color-[#1E90FF] hover:bg-color-[#1C86EE] text-white p-2 rounded-full transition duration-300"
          >
            <FaPencil className="text-WHITE text-xl " />
          </button>
          <span className="absolute bg-redPrimary text-white bottom-full mb-[-10px] w-max px-3 py-2 rounded-lg  border border-gray-300 shadow-lg text-[16px] font-medium opacity-0 group-hover:opacity-100 transition pointer-events-none z-10">
            Editar informações
          </span>
        </div>
      </div>
      <div className="p-6">
        {purchaseOrder?.votesApproved && (
          <div className="flex gap-4 mb-4">
            <div className="bg-yellow mb-4 p-4 w-1/3 text-white font-bold rounded">
              <h2>
                Pre-aprovada por:{" "}
                {purchaseOrder.votesApproved[0]?.requestorName}
              </h2>
            </div>
            {purchaseOrder?.votesApproved[1] && (
              <div className="bg-green mb-4 p-4 w-1/3 text-white font-bold rounded">
                <h2>
                  Aprovado por: {purchaseOrder.votesApproved[1]?.requestorName}
                </h2>
              </div>
            )}
          </div>
        )}

        <div className="text-[22px] text-gray-800 flex border-b mb-3 items-center justify-between">
          <span className="items-center bg-gray-200 text-gray-900 mb-4 rounded">
            {data.supplierName}
            <span className="flex items-center gap-1">
              <span
                className={`p-2 flex items-center justify-center text-white rounded-md ${getStatusColor(
                  data.status
                )}`}
              >
                <span className="text-[15px] font-medium capitalize">
                  {STATUS_LABELS[data.status] || data.status}
                </span>
              </span>
            </span>
          </span>

          {(data.status === "pending" || data.status === "pre-approved") &&
            getCompanyId() !== "*" && (
              <div className="flex gap-2">
                <button
                  className={
                    "rounded px-4 py-2 text-sm font-semibold transition bg-greenPrimary text-white " +
                    (selectedAction === "approve"
                      ? "ring-2 ring-green-500 text-green-700"
                      : "hover:bg-green-700")
                  }
                  onClick={handleApprove}
                >
                  Aprovar
                </button>
                <button
                  className={
                    "rounded px-4 py-2 text-sm font-semibold transition bg-redPrimary text-white " +
                    (selectedAction === "reject"
                      ? "ring-2 ring-red-500 text-red-700"
                      : "hover:bg-yellow-600")
                  }
                  onClick={handleReject}
                >
                  Reprovar
                </button>
                <button
                  className={
                    "rounded px-4 py-2 text-sm font-semibold transition bg-gray text-white " +
                    (selectedAction === "remove"
                      ? "ring-2 ring-gray-500 text-gray-700"
                      : "hover:bg-red-700")
                  }
                  onClick={handleRemove}
                >
                  Cancelar
                </button>
              </div>
            )}
        </div>
        <div className="border-b mb-4 pb-4">
          <h2 className="mb-2 font-medium">RESUMO FINANCEIRO</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <GenericInfoField
              id="discountAmount"
              spanSize="col-span-1"
              label="Valor de Desconto"
              value={newMaskCurrency(data.financial.discountAmount ?? 0)}
            />
            <GenericInfoField
              id="deliveryForecast"
              spanSize="col-span-1"
              label="Valor de Entrega"
              value={newMaskCurrency(
                typeof data.financial.deliveryForecast === "number"
                  ? data.financial.deliveryForecast
                  : 0
              )}
            />
            <GenericInfoField
              id="valueOfLabor"
              spanSize="col-span-1"
              label="Valor de Serviço"
              value={newMaskCurrency(data.financial.valueOfLabor ?? 0)}
            />
            <GenericInfoField
              id="taxes"
              spanSize="col-span-1"
              label="Valor de Impostos"
              value={newMaskCurrency(data.financial.taxes || 0)}
            />
          </div>

          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 w-full">
            {/* Botão de Parcelas */}
            <div className="relative w-full sm:w-auto">
              <button
                type="button"
                className="w-full sm:w-auto bg-gray-100 border rounded px-3 py-2 hover:bg-gray-200 transition flex items-center justify-between text-sm font-medium"
                onClick={() => setShowAllInstallments((prev) => !prev)}
              >
                <div className="flex items-center gap-2 text-white bg-[#e44131] px-3 py-1 rounded text-[16px]">
                  Quantidade parcelas: {data.financial.instalment.length}
                  <svg
                    className={`w-4 h-4 transition-transform ${showAllInstallments ? "rotate-180" : ""
                      }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </button>

              {showAllInstallments && (
                <div className="absolute left-0 w-full max-w-sm flex flex-col gap-2 z-30 bg-white border rounded shadow p-3 mt-2">
                  {data.financial.instalment.map((installment, index) => (
                    <div
                      key={index}
                      className="text-sm text-gray-800 flex justify-between border-b pb-1"
                    >
                      <span>
                        Parcela {index + 1}
                        <p className="text-xs text-gray-500">
                          {installment.dueDate
                            ? new Date(installment.dueDate).toLocaleDateString(
                              "pt-BR"
                            )
                            : ""}
                        </p>
                      </span>
                      <span>{newMaskCurrency(installment.value)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Valor Total */}
            <div className="text-gray-800 w-full sm:w-auto">
              <p className="font-medium text-[17px] mb-1">Valor Total</p>
              <span className="text-lg font-semibold">
                {newMaskCurrency(data.financial.value)}
              </span>
            </div>
          </div>
        </div>

        <div></div>

        <div className="border-b mb-4 pb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <GenericInfoField
              id="typePayment"
              spanSize="col-span-1 sm:col-span-2 lg:col-span-2"
              label="Tipo de Pagamento"
              value={
                data.financial.typePayment === TYPE_PAYMENT.Pix
                  ? `${data.financial.typePayment} - ${data.financial.pixKey}`
                  : data.financial.typePayment
              }
            />

            <GenericInfoField
              id="costCenterName"
              spanSize="col-span-1"
              label="Centro de Custo"
              value={data.financial.costCenterName}
            />

            <GenericInfoField
              id="expectedDate"
              spanSize="col-span-1"
              label="Data de pagamento previsto"
              value={new Date(
                data.financial.expectedDate as Date
              )?.toLocaleDateString("pt-BR")}
            />
          </div>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <GenericInfoField
              id="categoryName"
              spanSize="col-span-1"
              label="Categoria"
              value={data.financial.categoryName}
            />

            {data.deliveryDate && (
              <GenericInfoField
                id="deliveryDate"
                spanSize="col-span-1"
                label="Data de Entrega"
                value={new Date(data.deliveryDate as Date)?.toLocaleDateString(
                  "pt-BR"
                )}
              />
            )}
          </div>
        </div>

        <div className="flex flex-col gap-2 mb-4">
          <p>Observações</p>
          <p className="border p-2">{purchaseOrder.observations}</p>
        </div>

        <div className="border-b mb-6 pb-4">
          <h2 className="text-lg font-semibold mb-2">
            Itens da Ordem de Compra
          </h2>
          {data.itens && data.itens.length > 0 ? (
            <div className="overflow-auto">
              <table className="min-w-full divide-y divide-gray-200 border rounded-md">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-4  py-2 text-left text-sm font-semibold text-gray-700">
                      Descrição
                    </th>
                    <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                      Qtd.
                    </th>
                    <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                      Preço de Custo
                    </th>
                    <Permission permission="menu" entity="inventory-pieces">
                      <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                        Ações
                      </th>
                    </Permission>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {data.itens.map((item, index) => (
                    <tr key={item._id || index} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-sm text-gray-900">
                        {item.description}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-900">
                        {item.qtd}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-900">
                        {item.value}
                      </td>
                      <Permission permission="menu" entity="inventory-pieces">
                        <td className="px-4 py-2 text-sm text-gray-900">
                          <ContextMenu
                            data={item}
                            actions={
                              [
                                {
                                  label: "Editar",
                                  action: () => {
                                    setAction({ eventName: "editItem", data });
                                  },
                                  permission: "edit:inventory-pieces",
                                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                  disabled: (data: any) =>
                                    (data.supplierPiece &&
                                      (!data.quantity || data.quantity == 0)) ||
                                    getCompanyId() === "*",
                                },
                              ] as ActionItem[]
                            }
                          />
                        </td>
                      </Permission>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-gray-500 italic">Nenhum item encontrado.</div>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between">
            <h2 className="mb-2 font-medium">DOCUMENTOS ANEXADOS</h2>
            <button
              onClick={() =>
                setAction({ eventName: "editBilletFiles", data: data })
              }
              className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 mb-5"
            >
              Adicionar Arquivos
            </button>
          </div>
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold mb-2 text-gray-600">
                Boletos
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {data.financial.billetFiles &&
                  (data.financial.billetFiles as string)
                    .split(",")
                    .filter((file) => file && file !== "nofile")
                    .map((file, index) => (
                      <div className="containerFile">
                        <FileLink key={`billet-${index}`} fileName={file} />
                        <button onClick={() => setShowDeleteFile(file)}><FaRegTrashAlt></FaRegTrashAlt></button>
                      </div>
                    ))}
              </div>
            </div>
            <div>
              <h3 className="text-sm font-semibold mb-2 text-gray-600">
                Notas Fiscais (NF-e)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {data.financial.nfFiles &&
                  (data.financial.nfFiles as string)
                    .split(",")
                    .filter((file) => file && file !== "nofile")
                    .map((file, index) => (
                      <div className="containerFile">
                        <FileLink key={`nf-${index}`} fileName={file} />
                        <button onClick={() => setShowDeleteFile(file)}><FaRegTrashAlt></FaRegTrashAlt></button>
                      </div>
                    ))}
              </div>
            </div>
            <div>
              <h3 className="text-sm font-semibold mb-2 text-gray-600">
                Comprovantes de Pagamento
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {data.financial.paymentProofFiles &&
                  (data.financial.paymentProofFiles as string)
                    .split(",")
                    .filter((file) => file && file !== "nofile")
                    .map((file, index) => (
                      <div className="containerFile">
                        <FileLink key={`proof-${index}`} fileName={file} />
                        <button onClick={() => setShowDeleteFile(file)}><FaRegTrashAlt></FaRegTrashAlt></button>
                      </div>
                    ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Modais de confirmação */}
      <ModalConfirmStatus
        open={modal !== null}
        confirmType={modal ?? "approve"}
        title={
          modal === "approve"
            ? "Confirmar Aprovação"
            : modal === "reject"
              ? "Confirmar Reprovação"
              : "Confirmar Cancelamento"
        }
        description={
          modal === "approve"
            ? "Tem certeza que deseja aprovar esta ordem de compra?"
            : modal === "reject"
              ? "Tem certeza que deseja reprovar esta ordem de compra?"
              : "Tem certeza que deseja cancelar esta ordem de compra?"
        }
        confirmLabel={
          modal === "approve"
            ? "Aprovar"
            : modal === "reject"
              ? "Reprovar"
              : "Cancelar"
        }
        cancelLabel="Voltar"
        loading={loading}
        onCancel={() => setModal(null)}
        onConfirm={
          modal === "approve"
            ? confirmApprove
            : modal === "reject"
              ? confirmReject
              : confirmRemove
        }
      />

      {isAction("edit") && (
        <ModalEditTable purchaseOrder={data} onClose={onClose} />
      )}
      {isAction("editItem") && (
        <ModalEditSubTable purchaseOrder={data} onClose={onClose} />
      )}
      {isAction("editBilletFiles") && (
        <ModalEditBilletFiles
          onClose={(data) => setAction({ eventName: "", data: data })}
        ></ModalEditBilletFiles>
      )}

      {showDeleteFile && <ModalDeleteFile onClose={() => setShowDeleteFile("")} file={showDeleteFile}></ModalDeleteFile>}
    </section>
  );
};

export default InfoTablePurchaseOrder;
